// components/fournisseurs/SupplierForm.tsx
import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource, patchResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface SupplierFormProps {
  supplier?: any | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function SupplierForm({ supplier, onClose, onSuccess }: SupplierFormProps) {
  const isEdit = !!supplier;

  const [formData, setFormData] = useState({
    raisonSociale: '',
    nomCommercial: '',
    nif: '',
    registreCommerce: '',
    contactPrincipal: '',
    fonctionContact: '',
    telephone: '',
    telephone2: '',
    email: '',
    plafond_credit: '',
    adresseFacturation: '',
    adresseLivraison: '',
    ville: '',
    pays: 'Guinée',
    conditionsPaiement: '30 jours Net',
    devise: 'GNF',
    rib: '',
    statut: 'Actif' as 'Actif' | 'Inactif' | 'Suspendu',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Chargement complet des données en modification
  useEffect(() => {
    if (!supplier) return;

    const delaiToText = (jours: number): string => {
      const map: Record<number, string> = {
        0: 'Comptant à Réception',
        15: '15 jours Net',
        30: '30 jours Net',
        45: '45 jours Net',
        60: '60 jours Net',
      };
      return map[jours] || '30 jours Net';
    };

    setFormData({
      raisonSociale: supplier.raisonSociale || '',
      nomCommercial: supplier.nom_commercial || '',
      nif: supplier.nif || '',
      registreCommerce: supplier.registre_commerce || '',
      contactPrincipal: supplier.contactPrincipal || '',
      fonctionContact: supplier.fonction_contact || '',
      telephone: supplier.telephone || '',
      telephone2: supplier.telephone_2 || '',
      email: supplier.email || '',
      plafond_credit: supplier.plafond_credit || '',
      adresseFacturation: supplier.adresseFacturation || '',
      adresseLivraison: supplier.adresseLivraison || '',
      ville: supplier.ville || '',
      pays: supplier.pays || 'Guinée',
      conditionsPaiement: delaiToText(supplier.conditionsPaiement),
      devise: supplier.devise?.code || 'GNF',
      rib: supplier.rib || '',
      statut: supplier.bloque ? 'Suspendu' : supplier.actif ? 'Actif' : 'Inactif',
    });
  }, [supplier]);

  const conditionsPaiementOptions = [
    'Comptant à Réception',
    '15 jours Net',
    '30 jours Net',
    '45 jours Net',
    '60 jours Net',
    'Acompte 30% + Solde',
    'Acompte 50% + Solde',
    'Acompte 70% + Solde',
  ];

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};

    if (!formData.raisonSociale.trim()) errs.raisonSociale = 'Raison sociale obligatoire';
    if (!formData.nif.trim()) errs.nif = 'NIF obligatoire';
    else if (!/^\d{12}$/.test(formData.nif)) errs.nif = 'Le NIF doit contenir 12 chiffres';
    if (!formData.contactPrincipal.trim()) errs.contactPrincipal = 'Contact obligatoire';
    if (!formData.telephone.trim()) errs.telephone = 'Téléphone obligatoire';
    if (!formData.email.trim()) errs.email = 'Email obligatoire';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Email invalide';
    if (!formData.adresseFacturation.trim()) errs.adresseFacturation = 'Adresse obligatoire';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const payload = {
      raison_sociale: formData.raisonSociale,
      nom_commercial: formData.nomCommercial || null,
      nif: formData.nif,
      plafond_credit: formData.plafond_credit || null,
      registre_commerce: formData.registreCommerce || null,
      contact_principal: formData.contactPrincipal,
      fonction_contact: formData.fonctionContact || null,
      telephone: formData.telephone,
      telephone_2: formData.telephone2 || null,
      email: formData.email,
      adresse_facturation: formData.adresseFacturation,
      adresse_livraison: formData.adresseLivraison || null,
      ville: formData.ville || null,
      pays: formData.pays,
      conditions_paiement: formData.conditionsPaiement,
      devise: formData.devise,
      rib: formData.rib || null,
      statut: formData.statut,
    };

    try {
      if (isEdit) {
        await updateResource(`fournisseurs`,supplier.id, payload);
      } else {
        await createResource('fournisseurs', payload);
      }
      toast.success("successfully saved");
      onSuccess?.();
      onClose();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const map: Record<string, string> = {
          raison_sociale: 'raisonSociale',
          nif: 'nif',
          contact_principal: 'contactPrincipal',
          telephone: 'telephone',
          email: 'email',
          adresse_facturation: 'adresseFacturation',
        };
        const formatted: Record<string, string> = {};
        Object.keys(apiErrors).forEach(key => {
          const field = map[key] || key;
          formatted[field] = apiErrors[key][0];
        });
        toast.error("Erreur chargement des données");
        setErrors(formatted);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-h-screen overflow-y-auto">
      <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* IDENTIFICATION & ADMINISTRATION */}
        <section>
          <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center">
            <i className="ri-building-line mr-2 text-blue-600"></i>
            Identification & Administration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Raison Sociale <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.raisonSociale} onChange={e => handleChange('raisonSociale', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.raisonSociale ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Ex: SOGUIPAMI SA" />
              {errors.raisonSociale && <p className="text-red-600 text-xs mt-1">{errors.raisonSociale}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom Commercial</label>
              <input type="text" value={formData.nomCommercial} onChange={e => handleChange('nomCommercial', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Ex: SOGUIPAMI" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                NIF <span className="text-red-500">*</span>
              </label>
              <input type="text" maxLength={12} value={formData.nif}
                onChange={e => handleChange('nif', e.target.value.replace(/\D/g, '').slice(0, 12))}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.nif ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="123456789012" />
              {errors.nif && <p className="text-red-600 text-xs mt-1">{errors.nif}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registre de Commerce</label>
              <input type="text" value={formData.registreCommerce} onChange={e => handleChange('registreCommerce', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="RCCM/GN/CKY/2020/B/1234" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Principal <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.contactPrincipal} onChange={e => handleChange('contactPrincipal', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.contactPrincipal ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="M. Alpha Diallo - Directeur Achats" />
              {errors.contactPrincipal && <p className="text-red-600 text-xs mt-1">{errors.contactPrincipal}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fonction du contact</label>
              <input type="text" value={formData.fonctionContact} onChange={e => handleChange('fonctionContact', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Directeur des Achats" />
            </div>
          </div>
        </section>

        {/* COORDONNÉES */}
        <section>
          <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center">
            <i className="ri-map-pin-line mr-2 text-green-600"></i>
            Coordonnées
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse de Facturation <span className="text-red-500">*</span>
              </label>
              <textarea rows={3} value={formData.adresseFacturation} onChange={e => handleChange('adresseFacturation', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.adresseFacturation ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Immeuble SOGUIPAMI, Kaloum, Conakry"></textarea>
              {errors.adresseFacturation && <p className="text-red-600 text-xs mt-1">{errors.adresseFacturation}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse de Livraison (si différente)
              </label>
              <textarea rows={2} value={formData.adresseLivraison} onChange={e => handleChange('adresseLivraison', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Laisser vide si identique à l'adresse de facturation"></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone Principal <span className="text-red-500">*</span>
              </label>
              <input type="tel" value={formData.telephone} onChange={e => handleChange('telephone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.telephone ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="+224 622 123 456" />
              {errors.telephone && <p className="text-red-600 text-xs mt-1">{errors.telephone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone Secondaire</label>
              <input type="tel" value={formData.telephone2} onChange={e => handleChange('telephone2', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="+224 664 789 012" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="commandes@societe.gn" />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input type="text" value={formData.ville} onChange={e => handleChange('ville', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Conakry" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
              <input type="text" value={formData.pays} onChange={e => handleChange('pays', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Guinée" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select value={formData.statut} onChange={e => handleChange('statut', e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="Actif">Actif</option>
                <option value="Inactif">Inactif</option>
                <option value="Suspendu">Suspendu</option>
              </select>
            </div>
          </div>
        </section>

        {/* CONDITIONS FINANCIÈRES */}
        <section>
          <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center">
            <i className="ri-money-dollar-circle-line mr-2 text-purple-600"></i>
            Conditions Financières
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de Paiement</label>
              <select value={formData.conditionsPaiement} onChange={e => handleChange('conditionsPaiement', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {conditionsPaiementOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Devise de Facturation</label>
              <select value={formData.devise} onChange={e => handleChange('devise', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="GNF">GNF - Franc Guinéen</option>
                <option value="USD">USD - Dollar Américain</option>
                <option value="EUR">EUR - Euro</option>
                <option value="XOF">XOF - Franc CFA</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RIB / IBAN (facultatif)</label>
              <input type="text" value={formData.rib} onChange={e => handleChange('rib', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Informations bancaires pour les virements" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plafond de Crédit (facultatif)</label>
              <input type="text" value={formData.plafond_credit} onChange={e => handleChange('plafond_credit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Ex: 5000000" />
            </div>
          </div>
        </section>

        {/* BOUTONS */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            Annuler
          </button>
          <button type="submit" disabled={loading}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-70">
            {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer le fournisseur'}
          </button>
        </div>
      </form>
    </div>
  );
}